// Delivery CLINs — the contract's priced line-item schedule, read from the
// base award and any applied modifications. Hierarchical (top-level CLINs expand to their sub-items),
// filterable (Base / Options / Not priced), and role-masked on the money
// column. Row click opens the read-only CLIN detail drawer. Data comes from
// ryugu (which proxies pangaea); the per-CLIN deliverable counts are the
// contract model's deliverables with a schedule rule on that CLIN (D112 #6).
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraDataTable, SpecraEmptyState, SpecraFilterToolbar, SpecraInlineBanner, SpecraPageHeader, SpecraSegmentedControl, specraExpandableRowIds } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ChevronsDownUp, ChevronsUpDown, Search } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MaskedField } from '../../../../lib/access/MaskedField';
import { programRef } from '../../../../lib/access/types';
import { EmptyState } from '../../_shared/components/PageStates';
import { parsePwsSections } from '../../_shared/components/PwsChips';
import { formatMoney } from '../../_shared/lib/recordLabels';
import { useDuties } from '../../Deliverables/hooks';
import { useProgramModifications } from '../../Docs/hooks';
import { GlossaryText } from '../../Glossary/GlossaryText';
import { useContractCLINs } from '../contractHooks';
import { popHooks } from '../operationalHooks';
import { ClinDetailDrawer } from './ClinDetailDrawer';
// Top-level CLINs per page. OMEGA-scale programs (≈12 top-level lines) fit on a
// single page; larger schedules paginate.
const PARENTS_PER_PAGE = 25;
// The line's own source, where one is recorded. A line with no stamp may
// still have come from a modification (the Modification Center's apply does
// not stamp), so an unstamped line claims nothing.
export function lineSource(c) {
    const verb = {
        ADDED: 'Added',
        MODIFIED: 'Changed',
        REMOVED: 'Removed'
    }[(c.amendmentStatus ?? '').toUpperCase()];
    if (!verb) return '';
    return c.lastModifiedIn ? `${verb} by mod ${c.lastModifiedIn}` : `${verb} by a modification`;
}
// What the page can truthfully say its lines were read from (D86: never a
// source the data doesn't support). The base award alone only when the
// program has no applied modification and no line carries a mod stamp.
export function provenanceBasis(clins, mods) {
    if (!mods) return {
        basis: 'unknown',
        appliedMods: 0
    };
    const applied = new Set(mods.filter((m)=>(m.status ?? '').toLowerCase() === 'applied').map((m)=>(m.modificationNumber ?? '').toUpperCase()));
    const stamped = clins.some((c)=>!!c.amendmentStatus);
    if (applied.size === 0 && !stamped) return {
        basis: 'award',
        appliedMods: 0
    };
    return {
        basis: 'award+mods',
        appliedMods: applied.size
    };
}
// Only what is known: an applied modification may not have touched a single
// line, so the award+mods wording never says the lines were read from it.
function provenanceShort(p) {
    if (p.basis === 'award') return 'read from the base award';
    return 'read from the contract documents';
}
function provenanceNote(p) {
    if (p.basis === 'award') return 'Every line item on this page was read from the base award document.';
    if (p.basis === 'unknown') return 'Line items on this page were read from the contract documents.';
    const mods = p.appliedMods > 0 ? `${p.appliedMods} applied modification${p.appliedMods === 1 ? '' : 's'}` : 'its modifications';
    return `Line items on this page come from the base award, plus any changes from ${mods}. A line's badge names the modification that added or changed it, where one is recorded.`;
}
// A line item carries no independent award when it's flagged NSP, or when it's
// a sub-item with no award of its own (its cost rides on the parent).
function isNspRow(c) {
    const award = c.pricingAwardAmount ?? 0;
    return !!c.pricingIsNsp || !!c.isSublineItem && award === 0;
}
const OPTIONAL_TASK_TITLE_RE = /\boptional\s+task\s*#?\s*(\d+)/i;
function optionalTaskOf(c) {
    const fromText = (t)=>Number(OPTIONAL_TASK_TITLE_RE.exec(t ?? '')?.[1] ?? 0);
    const titled = fromText(c.optionalTaskTitle) || fromText(c.shortTitle);
    if (c.isOptionalTask) return c.optionalTaskNumber || titled;
    if ((c.optionType ?? '').toLowerCase() === 'optional_task') {
        return c.optionalTaskNumber || fromText(c.popPeriodName) || titled || c.optionPeriodNumber || 0;
    }
    return titled > 0 ? titled : null;
}
function periodInfo(c, parent, optionNumbers) {
    const task = optionalTaskOf(c) ?? (parent ? optionalTaskOf(parent) : null);
    if (task !== null) {
        return {
            label: task > 0 ? `Optional task ${task}` : 'Optional task',
            variant: 'info',
            group: 'option'
        };
    }
    const t = (c.optionType || parent?.optionType || '').toLowerCase();
    if (!t || t === 'base') return {
        label: 'Base',
        variant: 'neutral',
        group: 'base'
    };
    const n = c.optionPeriodNumber || parent?.optionPeriodNumber || Number(/^option\s+period\s+(\d+)/i.exec(c.popPeriodName || parent?.popPeriodName || '')?.[1] ?? 0);
    if (n > 0 && optionNumbers && !optionNumbers.has(n)) {
        return {
            label: 'Unassigned',
            variant: 'warning',
            group: 'unassigned'
        };
    }
    return {
        label: n > 0 ? `Option ${n}` : 'Option',
        variant: 'info',
        group: 'option'
    };
}
function qtyLabel(c) {
    if (!c.quantity) return '—';
    return c.unit ? `${c.quantity} ${c.unit}` : String(c.quantity);
}
function pwsLabel(c) {
    const secs = parsePwsSections(c.pwsSections);
    return secs.length > 0 ? `PWS ${secs.join(', ')}` : '';
}
function extractErrorMessage(err) {
    if (err instanceof Error) return err.message;
    if (typeof err === 'string') return err;
    try {
        return JSON.stringify(err);
    } catch  {
        return 'Unknown error';
    }
}
const clinId = (c)=>c.id;
export function CLINsTab({ programId }) {
    const navigate = useNavigate();
    const { data, isLoading, error } = useContractCLINs(programId);
    const { data: dutyData } = useDuties(programId, {
        includeRules: true
    });
    const { data: modData } = useProgramModifications(programId);
    const ref = programRef(programId);
    const [query, setQuery] = useState('');
    const [filter, setFilter] = useState('all');
    const [page, setPage] = useState(0);
    const [selectedClin, setSelectedClin] = useState(null);
    // null → untouched (first parent open by default). A list → the expanded
    // top-level CLIN ids.
    const [expanded, setExpanded] = useState(null);
    const clins = useMemo(()=>data?.clins ?? [], [
        data?.clins
    ]);
    const { data: periodsData } = popHooks.useList({
        programId
    });
    // The contract's option periods; null until they load, so nothing reads
    // "Unassigned" on a guess.
    const optionNumbers = useMemo(()=>periodsData ? new Set(periodsData.items.map((p)=>p.optionNumber).filter((n)=>n > 0)) : null, [
        periodsData
    ]);
    const provenance = useMemo(()=>provenanceBasis(clins, modData?.items), [
        clins,
        modData?.items
    ]);
    // Per-CLIN deliverables: each schedule rule names the CLIN row it bills
    // to (rule.clin_id is the CLIN's id), so a CLIN's deliverables are the
    // distinct deliverables with a rule on it.
    const deliverablesByClin = useMemo(()=>{
        const m = new Map();
        for (const r of dutyData?.rules ?? []){
            if (!r.clinId) continue;
            const set = m.get(r.clinId) ?? new Set();
            set.add(r.dutyId);
            m.set(r.clinId, set);
        }
        return m;
    }, [
        dutyData?.rules
    ]);
    // Hierarchy: top-level CLINs, each with its sub-items (natural CLIN order).
    const { topLevel, childrenOf } = useMemo(()=>{
        const byNum = new Map();
        const tops = [];
        for (const c of clins){
            if (c.isSublineItem && c.parentClinNumber) {
                const arr = byNum.get(c.parentClinNumber) ?? [];
                arr.push(c);
                byNum.set(c.parentClinNumber, arr);
            } else {
                tops.push(c);
            }
        }
        const natural = (a, b)=>String(a.clinNumber ?? '').localeCompare(String(b.clinNumber ?? ''), undefined, {
                numeric: true,
                sensitivity: 'base'
            });
        tops.sort(natural);
        for (const arr of byNum.values())arr.sort(natural);
        return {
            topLevel: tops,
            childrenOf: byNum
        };
    }, [
        clins
    ]);
    const allChildren = useCallback((c)=>c.isSublineItem ? undefined : childrenOf.get(c.clinNumber ?? ''), [
        childrenOf
    ]);
    const openIds = expanded ?? (topLevel[0] ? [
        clinId(topLevel[0])
    ] : []);
    const anyExpanded = topLevel.some((p)=>(allChildren(p)?.length ?? 0) > 0 && openIds.includes(clinId(p)));
    const toggleAll = ()=>setExpanded(anyExpanded ? [] : specraExpandableRowIds(topLevel, clinId, allChildren).map(String));
    // Filter-chip counts run over EVERY line item (parents + sub-items).
    const counts = useMemo(()=>{
        let base = 0;
        let option = 0;
        let nsp = 0;
        for (const c of clins){
            const parent = c.isSublineItem ? topLevel.find((p)=>p.clinNumber === c.parentClinNumber) : c;
            const { group } = periodInfo(c, parent === c ? undefined : parent, optionNumbers);
            if (group === 'base') base += 1;
            else if (group === 'option') option += 1;
            if (isNspRow(c)) nsp += 1;
        }
        return {
            all: clins.length,
            base,
            option,
            nsp
        };
    }, [
        clins,
        topLevel,
        optionNumbers
    ]);
    // The NSP band's explanation names the actual not-yet-applied modifications
    // (draft/pending) rather than hard-coding contract-specific mod numbers — so
    // the sentence stays true for any program and never asserts a draft state
    // that isn't on record.
    const nspBandDetail = useMemo(()=>{
        const pending = (modData?.items ?? []).filter((m)=>{
            const s = (m.status ?? '').toLowerCase();
            return s === 'draft' || s === 'pending';
        }).map((m)=>(m.modificationNumber ?? '').toUpperCase()).filter(Boolean).sort();
        const unique = [
            ...new Set(pending)
        ];
        if (unique.length === 0) {
            return 'Award and funded amounts land with the exercise modifications; until those are applied, most of this schedule carries no dollar value yet.';
        }
        const list = unique.length === 1 ? unique[0] : unique.length === 2 ? `${unique[0]} and ${unique[1]}` : `${unique.slice(0, -1).join(', ')}, and ${unique[unique.length - 1]}`;
        const verb = unique.length === 1 ? 'is' : 'are';
        return `Award and funded amounts land with the exercise modifications — ${list} ${verb} not yet applied, so most of this schedule carries no dollar value yet.`;
    }, [
        modData?.items
    ]);
    // Search + chip filter, applied per top-level group. A group survives if the
    // parent matches, or if any of its sub-items match.
    const q = query.trim().toLowerCase();
    // useCallback so these stay stable across renders and can be listed as memo
    // deps (each closes over exactly one of q / filter).
    const hitsQuery = useCallback((c)=>!q || (c.clinNumber ?? '').toLowerCase().includes(q) || (c.shortTitle || c.description || '').toLowerCase().includes(q) || pwsLabel(c).toLowerCase().includes(q), [
        q
    ]);
    const passesFilter = useCallback((parent)=>{
        if (filter === 'all') return true;
        const { group } = periodInfo(parent, undefined, optionNumbers);
        if (filter === 'base') return group === 'base';
        if (filter === 'option') return group === 'option';
        return isNspRow(parent); // nsp
    }, [
        filter,
        optionNumbers
    ]);
    const filteredGroups = useMemo(()=>{
        const groups = [];
        for (const parent of topLevel){
            if (!passesFilter(parent)) continue;
            const kids = childrenOf.get(parent.clinNumber ?? '') ?? [];
            const parentHit = hitsQuery(parent);
            const kidHits = q ? kids.filter(hitsQuery) : kids;
            if (!parentHit && kidHits.length === 0) continue;
            groups.push({
                parent,
                children: q ? kidHits : kids
            });
        }
        return groups;
    }, [
        topLevel,
        childrenOf,
        q,
        hitsQuery,
        passesFilter
    ]);
    // The sub-items a parent shows: while searching, only the matching ones.
    const visibleChildren = useMemo(()=>{
        const m = new Map();
        for (const g of filteredGroups)m.set(clinId(g.parent), g.children);
        return m;
    }, [
        filteredGroups
    ]);
    const getRowChildren = useCallback((c)=>visibleChildren.get(clinId(c)), [
        visibleChildren
    ]);
    const totalPages = Math.max(1, Math.ceil(filteredGroups.length / PARENTS_PER_PAGE));
    const safePage = Math.min(page, totalPages - 1);
    const pageStart = safePage * PARENTS_PER_PAGE;
    const pageGroups = filteredGroups.slice(pageStart, pageStart + PARENTS_PER_PAGE);
    const pageParents = pageGroups.map((g)=>g.parent);
    // Search forces every matching group open.
    const isOpen = (c)=>!!q || openIds.includes(clinId(c));
    const tableExpandedIds = q ? filteredGroups.map((g)=>clinId(g.parent)) : openIds;
    // A toggle while searching flips that group's own state, not the forced set.
    const onExpandedChange = (ids)=>{
        if (!q) {
            setExpanded(ids.map(String));
            return;
        }
        const next = new Set(ids.map(String));
        const toggled = tableExpandedIds.find((id)=>!next.has(id));
        if (!toggled) return;
        setExpanded(openIds.includes(toggled) ? openIds.filter((id)=>id !== toggled) : [
            ...openIds,
            toggled
        ]);
    };
    // Range label counts the rows actually rendered (a parent, plus its visible
    // sub-items when expanded), out of the line items the search and filter
    // keep: every line item only when neither narrows the schedule (SAD-856).
    const renderedRows = (g)=>1 + (isOpen(g.parent) ? g.children.length : 0);
    const rowsBefore = filteredGroups.slice(0, pageStart).reduce((n, g)=>n + renderedRows(g), 0);
    const rowsOnPage = pageGroups.reduce((n, g)=>n + renderedRows(g), 0);
    const kept = q || filter !== 'all' ? filteredGroups.reduce((n, g)=>n + 1 + g.children.length, 0) : clins.length;
    const rangeLabel = rowsOnPage === 0 ? '0 of 0' : `${rowsBefore + 1}–${rowsBefore + rowsOnPage} of ${kept}`;
    if (isLoading) return /*#__PURE__*/ _jsx("div", {
        className: "text-muted-foreground",
        children: "Loading CLINs…"
    });
    if (error) {
        return /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
            tone: "danger",
            variant: "framed",
            children: [
                "Failed to load CLINs: ",
                extractErrorMessage(error)
            ]
        });
    }
    if (clins.length === 0) {
        return /*#__PURE__*/ _jsx(EmptyState, {
            title: "No CLINs yet",
            description: "CLINs are the priced line items in the contract award. They populate automatically when an award document is extracted, or via the upcoming Phase 0b conversion mechanic."
        });
    }
    const topCount = topLevel.length;
    const subCount = clins.length - topCount;
    const total = data?.total ?? BigInt(0);
    const truncated = total > BigInt(clins.length);
    // Parent and sub-item share columns but differ in weight and value
    // semantics; a sub-item inherits its parent's period.
    const columns = [
        {
            id: 'clin',
            header: 'CLIN',
            className: 'whitespace-nowrap',
            cell: (c, meta)=>/*#__PURE__*/ _jsx("span", {
                    className: `font-mono tabular-nums ${meta.depth === 0 ? 'font-medium text-foreground' : 'text-muted-foreground'}`,
                    children: c.clinNumber
                })
        },
        {
            id: 'title',
            header: 'Title',
            cell: (c, meta)=>/*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: meta.depth === 0 ? 'font-medium text-foreground' : 'text-foreground',
                            children: [
                                c.shortTitle || c.description ? /*#__PURE__*/ _jsx(GlossaryText, {
                                    text: c.shortTitle || c.description
                                }) : '—',
                                lineSource(c) && /*#__PURE__*/ _jsx(SpecraBadge, {
                                    variant: "info",
                                    className: "ml-2 align-middle",
                                    "data-testid": Testids.deliveryOperational.clinSourceBadge,
                                    "data-amendment-status": c.amendmentStatus,
                                    "data-mod": c.lastModifiedIn || undefined,
                                    children: lineSource(c)
                                })
                            ]
                        }),
                        pwsLabel(c) && /*#__PURE__*/ _jsx("div", {
                            className: "text-[length:var(--fs-ui-meta)] tabular-nums text-muted-foreground",
                            children: pwsLabel(c)
                        })
                    ]
                })
        },
        {
            id: 'type',
            header: 'Type',
            className: 'whitespace-nowrap text-muted-foreground',
            cell: (c)=>c.contractType || '—'
        },
        {
            id: 'period',
            header: 'Period',
            className: 'whitespace-nowrap',
            cell: (c, meta)=>{
                const period = periodInfo(c, meta.parent, optionNumbers);
                return /*#__PURE__*/ _jsx(SpecraBadge, {
                    variant: period.variant,
                    children: period.label
                });
            }
        },
        {
            id: 'qty',
            header: 'Qty',
            align: 'right',
            className: 'whitespace-nowrap text-muted-foreground',
            cell: qtyLabel
        },
        {
            id: 'value',
            header: 'Value',
            align: 'right',
            className: 'whitespace-nowrap',
            cell: (c, meta)=>{
                const award = c.pricingAwardAmount ?? 0;
                const nsp = isNspRow(c);
                let valueSub = '';
                if (meta.depth > 0) valueSub = nsp ? `in ${c.parentClinNumber}` : '';
                else if (nsp) valueSub = 'allocated';
                else {
                    const funded = c.pricingFundedAmount ?? 0;
                    valueSub = award > 0 && funded >= award ? 'fully funded' : funded > 0 ? 'partially funded' : '';
                }
                return /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: nsp ? 'text-muted-foreground' : 'font-medium text-foreground',
                            children: nsp ? 'NSP' : /*#__PURE__*/ _jsx(MaskedField, {
                                fieldName: "pricing_award_amount",
                                resource: ref,
                                children: c.pricingIsTbd && award === 0 ? 'TBD' : formatMoney(award)
                            })
                        }),
                        valueSub && /*#__PURE__*/ _jsx("div", {
                            className: "text-[length:var(--fs-ui-meta)] text-muted-foreground",
                            children: valueSub
                        })
                    ]
                });
            }
        },
        {
            id: 'deliv',
            header: 'Deliv.',
            align: 'right',
            className: 'text-muted-foreground',
            cell: (c, meta)=>{
                // A parent counts its own deliverables and its sub-items', each once.
                const delivSet = new Set(deliverablesByClin.get(c.id) ?? []);
                if (meta.depth === 0) for (const k of childrenOf.get(c.clinNumber ?? '') ?? [])for (const d of deliverablesByClin.get(k.id) ?? [])delivSet.add(d);
                return delivSet.size || '—';
            }
        }
    ];
    const pageButton = (testId)=>({
            'data-testid': testId
        });
    return /*#__PURE__*/ _jsxs("div", {
        className: "pb-4",
        children: [
            /*#__PURE__*/ _jsx(SpecraPageHeader, {
                title: "CLINs",
                description: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        clins.length,
                        " line item",
                        clins.length === 1 ? '' : 's',
                        " · ",
                        topCount,
                        " top-level,",
                        ' ',
                        subCount,
                        " sub-item",
                        subCount === 1 ? '' : 's',
                        " · ",
                        provenanceShort(provenance)
                    ]
                }),
                actions: /*#__PURE__*/ _jsxs(SpecraButton, {
                    "data-testid": Testids.deliveryOperational.clinCollapseAll,
                    onClick: toggleAll,
                    size: "sm",
                    variant: "secondary",
                    children: [
                        anyExpanded ? /*#__PURE__*/ _jsx(ChevronsDownUp, {
                            "aria-hidden": "true"
                        }) : /*#__PURE__*/ _jsx(ChevronsUpDown, {
                            "aria-hidden": "true"
                        }),
                        anyExpanded ? 'Collapse all' : 'Expand all'
                    ]
                })
            }),
            counts.nsp > 0 && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                className: "mt-[var(--space-4)]",
                tone: "warning",
                title: `${counts.nsp} of ${clins.length} line items are Not Separately Priced.`,
                action: /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": Testids.deliveryOperational.clinReviewMods,
                    onClick: ()=>navigate(`/delivery/programs/${programId}/docs?tab=modifications`),
                    size: "sm",
                    variant: "secondary",
                    children: "Review draft mods"
                }),
                children: nspBandDetail
            }),
            /*#__PURE__*/ _jsx(SpecraFilterToolbar, {
                className: "mt-[var(--space-4)]",
                search: {
                    'aria-label': 'Search line items',
                    'data-testid': Testids.deliveryOperational.clinSearchInput,
                    icon: /*#__PURE__*/ _jsx(Search, {
                        "aria-hidden": "true"
                    }),
                    onChange: (e)=>{
                        setQuery(e.target.value);
                        setPage(0);
                    },
                    placeholder: 'Search CLIN, title, or PWS ref…',
                    value: query
                },
                filters: /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                    label: "Filter line items",
                    onValueChange: (v)=>{
                        setFilter(v);
                        setPage(0);
                    },
                    options: [
                        {
                            value: 'all',
                            label: 'All',
                            count: counts.all
                        },
                        {
                            value: 'base',
                            label: 'Base',
                            count: counts.base
                        },
                        {
                            value: 'option',
                            label: 'Options',
                            count: counts.option
                        },
                        {
                            value: 'nsp',
                            label: 'Not priced',
                            count: counts.nsp
                        }
                    ].map((o)=>({
                            ...o,
                            testId: Testids.deliveryOperational.clinFilterChip
                        })),
                    size: "sm",
                    value: filter
                }),
                resultSummary: /*#__PURE__*/ _jsx("span", {
                    className: "tabular-nums",
                    children: rangeLabel
                })
            }),
            /*#__PURE__*/ _jsx(SpecraDataTable, {
                className: "mt-[var(--space-3)]",
                columns: columns,
                empty: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                    title: `No line item matches ${q ? `“${query}”` : 'this filter'}.`,
                    description: "Sub-items are searched by CLIN, title, and PWS reference.",
                    action: /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>{
                            setQuery('');
                            setFilter('all');
                            setPage(0);
                        },
                        size: "sm",
                        variant: "secondary",
                        children: "Clear search"
                    })
                }),
                expandedIds: tableExpandedIds,
                expandToggleTestId: ()=>Testids.deliveryOperational.clinExpandToggle,
                getExpandToggleLabel: (_, open)=>open ? 'Collapse sub-items' : 'Expand sub-items',
                getExpandToggleProps: (c)=>({
                        'data-clin': c.clinNumber
                    }),
                getRowActionLabel: (c)=>`View CLIN ${c.clinNumber ?? ''} detail`,
                getRowChildren: getRowChildren,
                getRowId: clinId,
                getRowProps: ()=>({
                        title: 'View CLIN detail'
                    }),
                onExpandedChange: onExpandedChange,
                onRowClick: (c)=>setSelectedClin(c),
                pagination: {
                    itemLabel: 'page',
                    nextButtonProps: pageButton(Testids.deliveryOperational.clinPageNext),
                    onPageChange: (p)=>setPage(p - 1),
                    page: safePage + 1,
                    pageCount: totalPages,
                    previousButtonProps: pageButton(Testids.deliveryOperational.clinPagePrev),
                    // The footer carries where the lines were read from; the range sits
                    // in the toolbar.
                    statusText: ()=>/*#__PURE__*/ _jsx("span", {
                            "data-testid": Testids.deliveryOperational.clinProvenanceNote,
                            "data-basis": provenance.basis,
                            children: truncated ? `Showing the first ${clins.length} of ${total.toString()} line items — full pagination lands in Phase 0b.` : provenanceNote(provenance)
                        })
                },
                rows: pageParents,
                rowTestId: Testids.deliveryOperational.clinRow,
                testId: Testids.deliveryOperational.clinsTable
            }),
            /*#__PURE__*/ _jsx(ClinDetailDrawer, {
                clin: selectedClin,
                programId: programId,
                isOpen: selectedClin !== null,
                onClose: ()=>setSelectedClin(null)
            })
        ]
    });
}
